import React, { SetStateAction, Dispatch } from 'react';
import { connect } from 'react-redux';
import { Intl } from '@erecruit/core';
import { Resources, ListParams, Mutations } from '@erecruit/types';
import {
  FilterHeaderValuesTemplate,
  BaseSearchProps,
  OnDateRangeFilter,
  OnTimeRangeFilter,
  ResetDateFilter,
  FilterHandler,
  createRequestParams,
  createSearchPage,
  createColumnsHook,
} from '@erecruit/modules';
import { PositionsParams } from '../../App/types';
import {
  PositionSearchFilterData,
  selectors,
  searchPositions,
  exportPositions,
} from './state/reducer';
import {
  generateColumns,
  defaultVisibleColumns,
  parseTableFilters,
  PositionColumnType,
  getDefinedPositionReportColumns,
} from './columns';

const { FormattedMessage } = Intl;

const positionsTemplateValues: FilterHeaderValuesTemplate<PositionsParams>[] = [
  {
    value: 'All',
    label: <FormattedMessage id={messageIds.vms.positions.all} />,
  },
  {
    value: 'Open',
    icon: 'positions',
    label: <FormattedMessage id={messageIds.vms.positions.openPositionText} />,
  },
  {
    value: 'Closed',
    icon: 'closed-position',
    label: <FormattedMessage id={messageIds.vms.positions.closePositionText} />,
  },
  {
    value: 'PendingMyApproval',
    icon: 'approvals',
    label: <FormattedMessage id={messageIds.vms.positions.pendingMyApproval} />,
  },
];

export interface PositionSearchProps
  extends BaseSearchProps<
      Resources.PositionSearch,
      PositionSearchFilterData,
      Resources.PositionSearchFilter
    >,
    Intl.InjectedIntlProps {}

function getFilterImplementation(
  filterParams: Resources.PositionSearchParams,
  setFilterParams: Dispatch<SetStateAction<Resources.PositionSearchParams>>,
) {
  const filterByDate: OnDateRangeFilter = (fieldKey, range) => {
    setFilterParams({
      ...filterParams,
      filter: { ...filterParams.filter, [fieldKey]: range },
    });
  };

  const filterByTime: OnTimeRangeFilter = (fieldKey, range) => {
    setFilterParams({
      ...filterParams,
      filter: { ...filterParams.filter, [fieldKey]: range },
    });
  };

  const resetDateFilter: ResetDateFilter = dateFieldKey => {
    if (
      filterParams.filter &&
      !filterParams.filter[dateFieldKey as keyof Resources.PositionSearchFilter]
    )
      return;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [dateFieldKey as keyof Resources.PositionSearchFilter]: omit,
      ...other
    } = filterParams.filter as Resources.PositionSearchFilter;

    setFilterParams({
      ...filterParams,
      filter: { ...other },
    });
  };

  return {
    filterByDate,
    filterByTime,
    resetDateFilter,
  };
}

function generateColumnsFactory(
  filters: Record<string, FilterHandler>,
  filterData: Resources.PositionSearchFilter,
  locationSearch: string,
) {
  const filterByDate: OnDateRangeFilter = filters['filterByDate'];
  const filterByTime: OnTimeRangeFilter = filters['filterByTime'];
  const resetDateFilter: ResetDateFilter = filters['resetDateFilter'];

  return generateColumns(
    filterByDate,
    filterByTime,
    resetDateFilter,
    filterData,
    locationSearch,
  );
}

function getRequestParams(
  positionType: PositionsParams,
): ListParams<Resources.PositionSearchFilter> {
  return createRequestParams(
    {
      positionSearchType: Resources.PositionSearchType[positionType],
      searchTerm: '',
    },
    nameof<Resources.PositionSearch>(o => o.createdOn),
  );
}

const useColumns = createColumnsHook(
  getFilterImplementation,
  generateColumnsFactory,
);

const getReportColumns = (
  intl: Intl.InjectedIntl,
  visibleColumns: string[],
): Mutations.ColumnDefinition[] => {
  const allDefinedColumns = getDefinedPositionReportColumns(intl);
  const visibleColumnsIds = visibleColumns.map(
    visibleColumn => visibleColumn.split('.')[0],
  );
  return allDefinedColumns.filter(
    reportColumn =>
      visibleColumnsIds.findIndex(
        visibleColumnId => reportColumn.id === visibleColumnId,
      ) !== -1,
  );
};

export const PositionSearch = createSearchPage<
  Resources.PositionSearchFilter,
  PositionSearchProps,
  PositionColumnType,
  PositionsParams,
  Resources.PositionSearch,
  PositionSearchFilterData
>({
  title: <FormattedMessage id={messageIds.vms.positions.positions} />,
  searchTermPlaceholderText: 'Search Positions',
  baseQueryRoute: 'positionType',
  defaultSearchQuery: 'All',
  getReportColumns,
  alwaysVisibleColumns: ['name'],
  rowKey: nameof<Resources.PositionSearch>(o => o.id),
  filterHeaderValues: positionsTemplateValues,
  getDefaultVisibleColumns: () => defaultVisibleColumns,
  useColumns,
  getRequestParams,
  parseFilterParams: (filters, columns, filterParams) => {
    const parsedFilters = parseTableFilters(filters, columns);
    return {
      ...parsedFilters,
      positionSearchType: filterParams?.positionSearchType,
      searchTerm: filterParams?.searchTerm,
      estimatedStartDate: filterParams?.estimatedStartDate,
      estimatedEndDate: filterParams?.estimatedEndDate,
      startTime: filterParams?.startTime,
      endTime: filterParams?.endTime,
      createdOn: filterParams?.createdOn,
    };
  },
});

export default connect(selectors.selectPositionSearchState, {
  searchEntities: searchPositions,
  exportAction: exportPositions,
})(PositionSearch);
